import { contactPrivateDataSchema, newContactSchema, type ParsedContact } from '@aikoa/common'
import { useTranslations } from '@aikoa/i18n/react'
import { useRouter } from 'expo-router'
import { omit } from 'lodash-es'
import { useCallback, useMemo } from 'react'
import z from 'zod'
import { ContactForm } from '~/components/contacts/ContactForm'
import { FlowProvider } from '~/components/flows/FlowProvider.tsx'
import { PhaseWrapper } from '~/components/flows/phases/PhaseWrapper'
import { useFlowManager } from '~/components/flows/useFlowManager'
import { useAlert } from '~/components/GlobalAlert'
import { SectionContent } from '~/components/layout/Sections'
import { EditNotesSheet } from '~/components/notes/EditNotesSheet'
import { NotesDisplay } from '~/components/notes/NotesDisplay'
import { useAddContact, useUpdateContact, useUpdateContactNotes } from '~/data/contacts/useContacts'

const contactFlowDataSchema = z.object({
  ['contact-details']: newContactSchema,
})

type ContactFlowData = z.infer<typeof contactFlowDataSchema>

type PartialContactFlowData = {
  'contact-details': Omit<Partial<ContactFlowData['contact-details']>, 'consent_confirmed'> & {
    consent_confirmed?: boolean
  }
}

const initialContactData = {
  name: '',
  email: undefined,
  phone: undefined,
  consent_confirmed: false,
  language: 'fi' as const,
}

type ContactFlowProps = {
  contact?: ParsedContact
  onContactSaved?: (contactId: string) => void
  onCancel?: () => void
  isInBottomSheet?: boolean
  className?: string
}

export function ContactFlow({
  contact,
  onContactSaved: onFlowComplete,
  onCancel: onClose,
  isInBottomSheet,
  className,
}: ContactFlowProps) {
  const { t } = useTranslations('contacts')
  const { showAlert } = useAlert()
  const router = useRouter()

  const addContactMutation = useAddContact()
  const updateContactMutation = useUpdateContact()

  const phases = useMemo(
    () => [
      {
        id: 'contact-details',
        title: t(($) => $.details.title),
        schema: newContactSchema,
        data: {
          name: contact?.name,
          email: contact?.email,
          phone: contact?.phone,
          consent_confirmed: contact?.consent_confirmed ?? false,

          language: contact?.language || 'fi',
        },
      },
    ],
    [contact, t]
  )

  const handleFlowComplete = useCallback(
    async (data: Partial<ContactFlowData> | undefined) => {
      const contactData = contactFlowDataSchema.safeParse(data)

      if (!contactData.success) {
        console.error('Invalid contact data:', z.treeifyError(contactData.error))

        return false
      }

      const contactFlowData = contactData.data?.['contact-details']

      try {
        let savedContact: ParsedContact | null = null

        if (contact) {
          savedContact = await updateContactMutation.mutateAsync({
            id: contact.id,
            ...omit(contactFlowData, 'consent_confirmed'),
          })
        } else if (
          contactFlowData?.name &&
          (contactFlowData.email || contactFlowData.phone) &&
          contactFlowData.consent_confirmed
        ) {
          savedContact = await addContactMutation.mutateAsync(contactFlowData)
        }

        if (savedContact) {
          const contactId = savedContact.id

          if (onFlowComplete) {
            onFlowComplete(contactId)
          } else {
            router.replace({
              pathname: '/leads/[leadId]',
              params: { leadId: contactId },
            })
          }

          return true
        }

        return false
      } catch {
        showAlert({
          title: t(($) => $.error.title),
          description: t(($) => $.error.saveFailed),
          type: 'error',
        })

        return false
      }
    },
    [contact, onFlowComplete, router, showAlert, updateContactMutation, addContactMutation, t]
  )

  return (
    <FlowProvider
      phases={phases}
      // eslint-disable-next-line i18next/no-literal-string
      flowId={`contact_${contact?.id || 'new'}`}
      onComplete={handleFlowComplete}
      flowTitle={contact ? t(($) => $.edit.title) : t(($) => $.edit.addNew)}
      isLoading={addContactMutation.isPending || updateContactMutation.isPending}
      onClose={(resetFlow) => {
        resetFlow()

        if (onClose) {
          onClose()
        } else {
          router.replace({
            pathname: '/(authenticated)',
            params: { view: 'leads' },
          })
        }
      }}
      completeLabel={t(($) => $.save.label)}
      isInBottomSheet={isInBottomSheet}
      className={className}>
      <ContactFlowContent contact={contact} />
    </FlowProvider>
  )
}

function ContactFlowContent({ contact }: { contact?: ParsedContact }) {
  const { data: flowData, updateData } = useFlowManager()

  return (
    <ContactDetailsPhase
      isEditing={!!contact}
      contact={contact}
      formData={flowData?.['contact-details'] as PartialContactFlowData['contact-details']}
      onFormChange={(contactData) => updateData('contact-details', contactData)}
    />
  )
}

function ContactDetailsPhase({
  isEditing,
  formData,
  onFormChange,
  contact,
}: {
  isEditing: boolean
  formData?: PartialContactFlowData['contact-details']
  onFormChange: (data: PartialContactFlowData['contact-details']) => void
  contact?: ParsedContact
}) {
  const { t } = useTranslations('contacts')
  const description = isEditing ? t(($) => $.edit.editDescription) : t(($) => $.edit.addDescription)

  const privateData = contact?.private_data
    ? contactPrivateDataSchema.parse(contact.private_data)
    : { notes: null }

  const notes = privateData.notes
  const { mutate: updateNotes, isPending: isUpdatingNotes } = useUpdateContactNotes()

  const handleSaveNotes = (newNotes: string) => {
    if (!contact?.id) {
      return
    }

    updateNotes({ contactId: contact.id, notes: newNotes })
  }

  return (
    <PhaseWrapper contentClassName="gap-8">
      <SectionContent textClassName="text-pretty">{description}</SectionContent>

      <ContactForm
        formData={formData ?? initialContactData}
        onFormChange={onFormChange}
        className="mb-2 px-2"
      />

      {isEditing && contact && (
        <NotesDisplay
          notes={notes}
          canEdit
          editButton={
            <EditNotesSheet
              modalKey={`edit-contact-notes-${contact.id}`}
              currentNotes={notes ?? undefined}
              onSave={handleSaveNotes}
              isPending={isUpdatingNotes}
              triggerVariant="link"
              triggerSize="sm"
              triggerLabel={t(($) => $.edit.title)}
            />
          }
        />
      )}
    </PhaseWrapper>
  )
}
